import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { getValidToken } from '../utils/auth';
import { getMarketStatus } from '../utils/marketStatus';

const WS_URL = process.env.REACT_APP_WS_URL || `wss://${window.location.host}/ws`;

const MarketDataContext = createContext(null);

export function MarketDataProvider({ children }) {
  const [tickers, setTickers] = useState({});
  const [connected, setConnected] = useState(false);
  const [marketStatus, setMarketStatus] = useState(getMarketStatus());
  const [shortSymbols, setShortSymbols] = useState(new Set());
  const [breakoutLongs, setBreakoutLongs] = useState([]);
  const [breakoutShorts, setBreakoutShorts] = useState([]);
  const [top5History, setTop5History] = useState([]);
  const [convergenceSignals, setConvergenceSignals] = useState([]);
  const [updownWeight, setUpdownWeight] = useState({
    green1m: 0, red1m: 0, greenCount1m: 0, redCount1m: 0,
    green5m: 0, red5m: 0, greenCount5m: 0, redCount5m: 0,
    green30m: 0, red30m: 0, greenCount30m: 0, redCount30m: 0,
    minuteTS: null,
  });
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const mountedRef = useRef(true);

  // Update market status every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setMarketStatus(getMarketStatus());
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const connect = useCallback(async () => {
    if (!mountedRef.current) return;

    const token = await getValidToken();
    if (!token) {
      // Token refresh failed due to network error (auth failures redirect to login).
      // Schedule a retry so the reconnect loop doesn't break.
      if (mountedRef.current) {
        reconnectTimer.current = setTimeout(connect, 3000);
      }
      return;
    }
    if (!mountedRef.current) return;

    const ws = new WebSocket(`${WS_URL}?token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      if (mountedRef.current) setConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const messages = JSON.parse(event.data);

        // Handle breakout_signals event (2 tables: longs + shorts)
        if (messages && !Array.isArray(messages) && messages.ev === 'breakout_signals') {
          setBreakoutLongs(messages.longs || []);
          setBreakoutShorts(messages.shorts || []);
          return;
        }

        // Handle convergence_signals event
        if (messages && !Array.isArray(messages) && messages.ev === 'convergence_signals') {
          setConvergenceSignals(messages.signals || []);
          return;
        }

        // Handle top5_history event
        if (messages && !Array.isArray(messages) && messages.ev === 'top5_history') {
          setTop5History(messages.entries || []);
          return;
        }

        // Handle short_symbols event (object, not array)
        if (messages && !Array.isArray(messages) && messages.ev === 'short_symbols') {
          setShortSymbols(new Set(messages.symbols));
          return;
        }

        // Handle up/down by weight event
        if (messages && !Array.isArray(messages) && messages.ev === 'updown_weight') {
          setUpdownWeight({
            green1m: messages.green_1m || 0, red1m: messages.red_1m || 0,
            greenCount1m: messages.green_count_1m || 0, redCount1m: messages.red_count_1m || 0,
            green5m: messages.green_5m || 0, red5m: messages.red_5m || 0,
            greenCount5m: messages.green_count_5m || 0, redCount5m: messages.red_count_5m || 0,
            green30m: messages.green_30m || 0, red30m: messages.red_30m || 0,
            greenCount30m: messages.green_count_30m || 0, redCount30m: messages.red_count_30m || 0,
            minuteTS: messages.minute_ts || null,
          });
          return;
        }

        if (!Array.isArray(messages)) return;

        setTickers((prev) => {
          const updated = { ...prev };
          for (const msg of messages) {
            if (msg.ev !== 'AM') continue;
            if (!msg.op || msg.op <= 0) continue;

            const changePercent = ((msg.c - msg.op) / msg.op) * 100;

            updated[msg.sym] = {
              sym: msg.sym,
              price: msg.c,
              open: msg.op,
              changePercent: changePercent,
              volume: msg.v,
              timestamp: msg.s,
              premarketOpen: msg.pm_op || 0,
              mainOpen: msg.mm_op || 0,
            };
          }
          return updated;
        });
      } catch (e) {
        // ignore parse errors
      }
    };

    ws.onclose = () => {
      if (mountedRef.current) {
        setConnected(false);
        wsRef.current = null;
        reconnectTimer.current = setTimeout(connect, 3000);
      }
    };

    ws.onerror = () => {
      ws.close();
    };
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [connect]);

  return (
    <MarketDataContext.Provider value={{ tickers, connected, marketStatus, shortSymbols, breakoutLongs, breakoutShorts, top5History, convergenceSignals, updownWeight }}>
      {children}
    </MarketDataContext.Provider>
  );
}

export function useMarketContext() {
  const context = useContext(MarketDataContext);
  if (!context) {
    throw new Error('useMarketContext must be used within MarketDataProvider');
  }
  return context;
}
